// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import "@/App.css";
// Speed (Oct 2026): every page stylesheet stays loaded up front, in the SAME
// order as before, so code-splitting the pages below cannot change how any
// page looks. Only the page JavaScript is now loaded on demand.
import "@/pages/Pages.css";
import "@/components/Navigator.css";
import "@/components/QuestBoard.css";
import "@/components/RulesGuide.css";
import "@/components/Friends.css";
import "@/components/AvatarUpload.css";
import "@/components/TreasureWheel.css";
import "@/components/MixerPanel.css";
import "@/components/MyMusicPicker.css";
import "@/components/LiveRadioPicker.css";
import "@/components/BoardMixer.css";
import "@/pages/WarRoom.css";
import "@/components/CheckerGems.css";
import "@/components/BoardStyleGallery.css";
import "@/components/PieceSetShowcase.css";
import "@/components/Unlocks.css";
import "@/pages/Profile.css";
import "@/components/Lightbox.css";
import "@/pages/Store.css";
import "@/components/ShardPanel.css";
import "@/pages/Cards.css";
import "@/pages/TournamentBracket.css";
import "@/pages/ThroneRoom.css";
import "@/pages/Tournaments.css";
import "@/components/ClockRope.css";
import "@/pages/FactionWars.css";
import { lazy, Suspense, useEffect } from "react";
import "./pages/Legal.css";
import { BrowserRouter, Routes, Route, useLocation, matchPath } from "react-router-dom";
import Home from "@/pages/Home";
import GameErrorBoundary from "@/components/GameErrorBoundary";
// Pages load on demand (code splitting): each one downloads the first time
// it is opened, so the first screen appears faster.
const Games = lazy(() => import("@/pages/Games"));
const WarRoom = lazy(() => import("@/pages/WarRoom"));
const Puzzles = lazy(() => import("@/pages/Puzzles"));
const Settings = lazy(() => import("@/pages/Settings"));
const Profile = lazy(() => import("@/pages/Profile"));
const Store = lazy(() => import("@/pages/Store"));
const Cards = lazy(() => import("@/pages/Cards"));
const TournamentBracket = lazy(() => import("@/pages/TournamentBracket"));
const About = lazy(() => import("@/pages/About"));
const ThroneRoom = lazy(() => import("@/pages/ThroneRoom"));
const Tournaments = lazy(() => import("@/pages/Tournaments"));
const Game = lazy(() => import("@/pages/Game"));
const Puzzle = lazy(() => import("@/pages/Puzzle"));
const FourPlayer = lazy(() => import("@/pages/FourPlayer"));
const TwoPlayer = lazy(() => import("@/pages/TwoPlayer"));
const FactionWars = lazy(() => import("@/pages/FactionWars"));
import AuthCallback from "@/pages/AuthCallback";
import NavBar from "@/components/NavBar";
import TickerBar from "@/components/TickerBar";
import CardRewardToast from "@/components/CardRewardToast";
import CaptureFlourish from "@/components/CaptureFlourish";
import MusicDirector from "@/components/MusicDirector";
import { Toaster as SonnerToaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { PieceSetProvider } from "@/contexts/PieceSetContext";
import { BoardStyleProvider } from "@/contexts/BoardStyleContext";
import { FactionProvider } from "@/contexts/FactionContext";
import { SoundProvider } from "@/contexts/SoundContext";

import SiteFooter from "./components/SiteFooter";
import CookieBar from "./components/CookieBar";
const Legal = lazy(() => import("./pages/Legal"));

// Routes where the global NavBar must NOT render — focused gameplay screens.
const NAV_HIDDEN_PATTERNS = ["/game/:gameId", "/faction-wars/:gameId", "/puzzles/:difficulty", "/auth-callback"];

function shouldHideNav(pathname) {
  return NAV_HIDDEN_PATTERNS.some((p) => matchPath({ path: p, end: true }, pathname));
}

// After the first screen is up, quietly fetch the most-used pages and the
// player's piece artwork in the background so the next tap is instant.
function useIdlePrefetch() {
  useEffect(() => {
    const t = setTimeout(() => {
      import("@/pages/WarRoom");
      import("@/pages/Games");
      import("@/pages/TwoPlayer");
      import("@/pages/FourPlayer");
      import("@/pages/Game");
      let set = "alpha";
      try { set = window.localStorage.getItem("chess_piece_set") || "alpha"; } catch { /* ignore */ }
      const pub = process.env.PUBLIC_URL || "";
      ["w", "b"].forEach((side) => ["K", "Q", "R", "B", "N", "P"].forEach((p) => {
        fetch(`${pub}/pieces/${set}/${side}${p}.svg`).catch(() => {});
      }));
    }, 2500);
    return () => clearTimeout(t);
  }, []);
}

function AppRouter() {
  const location = useLocation();
  useIdlePrefetch();
  // Synchronous hash check — some OAuth providers land with the fragment on ANY path.
  // Handling it here prevents race conditions before route resolution.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  const hideNav = shouldHideNav(location.pathname);
  return (
    <>
      {!hideNav && <NavBar />}
      {!hideNav && <TickerBar />}
      <MusicDirector />
      <Suspense fallback={<div className="route-loading" role="status" aria-live="polite">Loading…</div>}>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/games" element={<WarRoom />} />
        <Route path="/battle-hall" element={<Games />} />
        <Route path="/two-player" element={<TwoPlayer />} />
        <Route path="/four-player" element={<FourPlayer />} />
        <Route path="/puzzles" element={<Puzzles />} />
        <Route path="/puzzles/:difficulty" element={<Puzzle />} />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/tournaments/:id" element={<TournamentBracket />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/store" element={<Store />} />
        <Route path="/cards" element={<Cards />} />
        <Route path="/about" element={<About />} />
        <Route path="/throne-room" element={<ThroneRoom />} />
        <Route path="/game/:gameId" element={<GameErrorBoundary><Game /></GameErrorBoundary>} />
        <Route path="/faction-wars/:gameId" element={<FactionWars />} />
        <Route path="/terms" element={<Legal doc="terms" />} />
        <Route path="/privacy" element={<Legal doc="privacy" />} />
        <Route path="/refunds" element={<Legal doc="refunds" />} />
        <Route path="/auth-callback" element={<AuthCallback />} />
      </Routes>
      </Suspense>
      {!hideNav && <SiteFooter />}
      {!hideNav && <CookieBar />}
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <PieceSetProvider>
        <BoardStyleProvider>
          <FactionProvider>
            <SoundProvider>
              <BrowserRouter>
                <AppRouter />
                {/* Card-reward toast infra (Feb 2026) — mounted at the
                    top of the tree so it fires regardless of route. */}
                <CardRewardToast />
                <CaptureFlourish />
                <SonnerToaster position="top-right" richColors closeButton />
              </BrowserRouter>
            </SoundProvider>
          </FactionProvider>
        </BoardStyleProvider>
      </PieceSetProvider>
    </AuthProvider>
  );
}

export default App;
